import { useEffect, useRef, useState } from 'react';
import { logoUrlForDomain, type GuestDto } from '@workshop-hst/shared';
import { api, ApiError } from '../lib/api';
import { useToast } from './Toast';

type Suggestion = { name: string; domain: string; logoUrl: string | null };

export type GuestFormState = {
  enabled: boolean;
  name: string;
  email: string;
  companyName: string;
  companyDomain: string;
  companyLogoUrl: string | null;
  companyLogoIncludesName: boolean;
  headshotUrl: string | null;
};

export function emptyGuestState(): GuestFormState {
  return {
    enabled: false,
    name: '',
    email: '',
    companyName: '',
    companyDomain: '',
    companyLogoUrl: null,
    companyLogoIncludesName: false,
    headshotUrl: null,
  };
}

export function guestStateFromDto(g: GuestDto | null | undefined): GuestFormState {
  if (!g) return emptyGuestState();
  const derivedLogoUrl = logoUrlForDomain(g.companyDomain);
  return {
    enabled: true,
    name: g.name,
    email: g.email ?? '',
    companyName: g.companyName,
    companyDomain: g.companyDomain ?? '',
    companyLogoUrl: g.companyLogoUrl && g.companyLogoUrl !== derivedLogoUrl ? g.companyLogoUrl : null,
    companyLogoIncludesName: g.companyLogoIncludesName,
    headshotUrl: g.headshotUrl ?? null,
  };
}

export function guestStateToInput(state: GuestFormState):
  | {
      name: string;
      email: string;
      companyName: string;
      companyDomain: string | null;
      companyLogoUrl: string | null;
      companyLogoIncludesName: boolean;
      headshotUrl: string | null;
    }
  | null {
  if (!state.enabled) return null;
  return {
    name: state.name.trim(),
    email: state.email.trim(),
    companyName: state.companyName.trim(),
    companyDomain: state.companyDomain.trim() || null,
    companyLogoUrl: state.companyLogoUrl,
    companyLogoIncludesName: state.companyLogoIncludesName,
    headshotUrl: state.headshotUrl,
  };
}

export function GuestSection({
  state,
  setState,
}: {
  state: GuestFormState;
  setState: (s: GuestFormState) => void;
}) {
  return (
    <div className="rounded-2xl border border-line bg-canvas/40 p-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-sm font-semibold tracking-tight">Guest speaker</h3>
          <p className="mt-1 text-xs text-ink-muted">
            Optional. Adds a co-host badge to the public card with their name and company logo.
          </p>
        </div>
        <label className="inline-flex cursor-pointer items-center gap-2 text-xs font-medium text-ink-muted">
          <input
            type="checkbox"
            checked={state.enabled}
            onChange={(e) => setState({ ...state, enabled: e.target.checked })}
            className="h-4 w-4 accent-ink"
          />
          Include guest
        </label>
      </div>

      {state.enabled ? (
        <div className="mt-5 space-y-4">
          <div className="flex items-start gap-5">
            <HeadshotUploader
              url={state.headshotUrl}
              guestName={state.name}
              onChange={(headshotUrl) => setState({ ...state, headshotUrl })}
            />
            <div className="flex-1 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field label="Guest name" required>
                <input
                  className="input"
                  value={state.name}
                  onChange={(e) => setState({ ...state, name: e.target.value })}
                  required={state.enabled}
                  placeholder="Madhavan Ramanujam"
                />
              </Field>
              <Field label="Guest email" required>
                <input
                  type="email"
                  className="input"
                  value={state.email}
                  onChange={(e) => setState({ ...state, email: e.target.value })}
                  required={state.enabled}
                  placeholder="madhavan@example.com"
                />
                <p className="mt-1 text-[11px] text-ink-subtle">
                  Admin-only. Used to send the guest their Zoom link + calendar invite when the workshop is published.
                </p>
              </Field>
            </div>
          </div>

          <CompanyPicker
            name={state.companyName}
            domain={state.companyDomain}
            logoUrl={state.companyLogoUrl}
            logoIncludesName={state.companyLogoIncludesName}
            required={state.enabled}
            onChange={({ name, domain, logoUrl, logoIncludesName }) =>
              setState({
                ...state,
                companyName: name,
                companyDomain: domain,
                companyLogoUrl: logoUrl,
                companyLogoIncludesName: logoIncludesName,
              })
            }
          />
        </div>
      ) : null}
    </div>
  );
}

function HeadshotUploader({
  url,
  guestName,
  onChange,
}: {
  url: string | null;
  guestName: string;
  onChange: (next: string | null) => void;
}) {
  const fileInput = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const toast = useToast();

  const initials = guestName
    .split(/\s+/)
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();

  const handlePick = async (file: File | null) => {
    if (!file) return;
    setUploading(true);
    try {
      const { url: uploaded } = await api.admin.uploadGuestHeadshot(file);
      onChange(uploaded);
    } catch (err) {
      toast.error('Upload failed', err instanceof ApiError ? err.message : String(err));
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  };

  return (
    <div className="flex flex-col items-center gap-2">
      <div className="relative">
        <div className="h-20 w-20 overflow-hidden rounded-full border border-line bg-canvas">
          {url ? (
            <img src={url} alt="" className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-xl font-bold text-ink-subtle">
              {initials || '?'}
            </div>
          )}
        </div>
        {uploading ? (
          <div className="absolute inset-0 flex items-center justify-center rounded-full bg-black/40 text-[11px] font-medium text-white">
            uploading…
          </div>
        ) : null}
      </div>
      <input
        type="file"
        ref={fileInput}
        accept="image/png,image/jpeg,image/webp,image/gif"
        className="hidden"
        onChange={(e) => handlePick(e.target.files?.[0] ?? null)}
      />
      <div className="flex items-center gap-2 text-[11px]">
        <button
          type="button"
          onClick={() => fileInput.current?.click()}
          className="font-semibold text-accent hover:underline"
          disabled={uploading}
        >
          {url ? 'Replace' : 'Upload headshot'}
        </button>
        {url ? (
          <>
            <span className="text-ink-subtle">·</span>
            <button
              type="button"
              onClick={() => onChange(null)}
              className="text-ink-subtle hover:text-ink"
              disabled={uploading}
            >
              Remove
            </button>
          </>
        ) : null}
      </div>
    </div>
  );
}

function CompanyPicker({
  name,
  domain,
  logoUrl,
  logoIncludesName,
  required,
  onChange,
}: {
  name: string;
  domain: string;
  logoUrl: string | null;
  logoIncludesName: boolean;
  required?: boolean;
  onChange: (next: {
    name: string;
    domain: string;
    logoUrl: string | null;
    logoIncludesName: boolean;
  }) => void;
}) {
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const debounceRef = useRef<number | null>(null);
  const blurTimer = useRef<number | null>(null);

  useEffect(() => {
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    if (name.trim().length < 2) {
      setSuggestions([]);
      setError(null);
      return;
    }
    setLoading(true);
    debounceRef.current = window.setTimeout(async () => {
      try {
        const res = await api.admin.suggestCompanies(name.trim());
        setSuggestions(res.suggestions);
        setError(null);
      } catch (_err) {
        setSuggestions([]);
        setError('Autocomplete unavailable — enter the company manually.');
      } finally {
        setLoading(false);
      }
    }, 280);
  }, [name]);

  const logo = logoUrl ?? logoUrlForDomain(domain);

  return (
    <div>
      <label className="block">
        <span className="text-xs uppercase tracking-wide text-ink-subtle">
          Company *
        </span>
        <div className="relative mt-1">
          <div className="flex items-center gap-2.5 rounded-xl border border-line bg-surface px-3 py-2 shadow-sm transition focus-within:border-ink/40 focus-within:ring-2 focus-within:ring-ink/10">
            {logo ? (
              <img
                src={logo}
                alt=""
                className="h-6 w-6 shrink-0 rounded bg-canvas object-contain"
                onError={(e) => ((e.currentTarget as HTMLImageElement).style.visibility = 'hidden')}
              />
            ) : (
              <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded bg-canvas text-[10px] font-bold text-ink-subtle">
                ?
              </span>
            )}
            <input
              type="text"
              value={name}
              onChange={(e) => onChange({ name: e.target.value, domain, logoUrl, logoIncludesName })}
              onFocus={() => setOpen(true)}
              onBlur={() => {
                blurTimer.current = window.setTimeout(() => setOpen(false), 160);
              }}
              placeholder="Start typing to search…"
              className="flex-1 bg-transparent text-sm outline-none placeholder:text-ink-subtle"
              autoComplete="off"
              required={required}
            />
            {loading ? (
              <span className="text-[11px] text-ink-subtle">searching…</span>
            ) : null}
          </div>

          {open && suggestions.length > 0 ? (
            <div className="absolute z-30 mt-1 w-full overflow-hidden rounded-xl border border-line bg-surface shadow-cardHover">
              {suggestions.map((s) => (
                <button
                  type="button"
                  key={`${s.name}-${s.domain}`}
                  onMouseDown={(e) => {
                    // mouseDown fires before blur, so we don't lose the click.
                    e.preventDefault();
                    onChange({ name: s.name, domain: s.domain, logoUrl: null, logoIncludesName: false });
                    setOpen(false);
                    if (blurTimer.current) window.clearTimeout(blurTimer.current);
                  }}
                  className="flex w-full items-center gap-3 px-3 py-2 text-left text-sm transition hover:bg-canvas"
                >
                  {s.logoUrl ? (
                    <img
                      src={s.logoUrl}
                      alt=""
                      className="h-6 w-6 shrink-0 rounded bg-canvas object-contain"
                      onError={(e) => ((e.currentTarget as HTMLImageElement).style.visibility = 'hidden')}
                    />
                  ) : (
                    <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded bg-canvas text-[10px] font-bold text-ink-subtle">
                      ?
                    </span>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="truncate font-medium">{s.name}</p>
                    <p className="truncate text-[11px] text-ink-subtle">{s.domain}</p>
                  </div>
                </button>
              ))}
            </div>
          ) : null}
        </div>
      </label>

      <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-[1fr,auto]">
        <Field label="Company website (for logo)">
          <input
            className="input"
            value={domain}
            onChange={(e) => onChange({ name, domain: e.target.value, logoUrl, logoIncludesName })}
            placeholder="example.com"
          />
        </Field>
        <div className="flex items-end pb-1">
          {logoUrl ? (
            <span className="text-[11px] text-ink-subtle">
              Custom uploaded logo
            </span>
          ) : domain ? (
            <span className="text-[11px] text-ink-subtle">
              Logo via DuckDuckGo Icons API
            </span>
          ) : (
            <span className="text-[11px] text-ink-subtle">No logo — free-form entry</span>
          )}
        </div>
      </div>

      <CompanyLogoUploader
        logoUrl={logoUrl}
        logoIncludesName={logoIncludesName}
        onChange={(nextLogoUrl) =>
          onChange({
            name,
            domain,
            logoUrl: nextLogoUrl,
            logoIncludesName: nextLogoUrl ? logoIncludesName : false,
          })
        }
        onIncludesNameChange={(next) => onChange({ name, domain, logoUrl, logoIncludesName: next })}
      />

      {error ? <p className="mt-1 text-xs text-ink-subtle">{error}</p> : null}
    </div>
  );
}

function CompanyLogoUploader({
  logoUrl,
  logoIncludesName,
  onChange,
  onIncludesNameChange,
}: {
  logoUrl: string | null;
  logoIncludesName: boolean;
  onChange: (next: string | null) => void;
  onIncludesNameChange: (next: boolean) => void;
}) {
  const fileInput = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const toast = useToast();

  const handlePick = async (file: File | null) => {
    if (!file) return;
    setUploading(true);
    try {
      const { url: uploaded } = await api.admin.uploadCompanyLogo(file);
      onChange(uploaded);
    } catch (err) {
      toast.error('Upload failed', err instanceof ApiError ? err.message : String(err));
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  };

  return (
    <div className="mt-3 rounded-xl border border-dashed border-line bg-canvas/50 px-3 py-2">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded bg-surface">
          {logoUrl ? (
            <img src={logoUrl} alt="" className="h-full w-full object-contain" />
          ) : (
            <span className="text-[10px] font-bold text-ink-subtle">LOGO</span>
          )}
        </div>
        <input
          type="file"
          ref={fileInput}
          accept="image/png,image/jpeg,image/webp,image/gif"
          className="hidden"
          onChange={(e) => handlePick(e.target.files?.[0] ?? null)}
        />
        <div className="min-w-0 flex-1">
          <p className="text-xs font-medium text-ink">Custom company logo</p>
          <p className="text-[11px] text-ink-subtle">Overrides the website-derived logo.</p>
        </div>
        <button
          type="button"
          onClick={() => fileInput.current?.click()}
          className="text-xs font-semibold text-accent hover:underline disabled:text-ink-subtle"
          disabled={uploading}
        >
          {uploading ? 'Uploading…' : logoUrl ? 'Replace' : 'Upload'}
        </button>
        {logoUrl ? (
          <button
            type="button"
            onClick={() => onChange(null)}
            className="text-xs text-ink-subtle hover:text-ink"
            disabled={uploading}
          >
            Remove
          </button>
        ) : null}
      </div>
      {logoUrl ? (
        <label className="mt-2 flex cursor-pointer items-center gap-2 text-[11px] text-ink-muted">
          <input
            type="checkbox"
            checked={logoIncludesName}
            onChange={(e) => onIncludesNameChange(e.target.checked)}
            className="h-3.5 w-3.5 accent-ink"
          />
          Use this as the full company label
        </label>
      ) : null}
    </div>
  );
}

function Field({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-wide text-ink-subtle">
        {label}
        {required ? ' *' : ''}
      </span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
